import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-hot-toast';
import { Plus, ArrowLeft, Edit2, Trash2, Power, Calculator, Info, X } from 'lucide-react';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import AllowancesSection, { defaultAllowances, allowancesFromConfig, allowancesToPayload } from './AllowancesSection';

const TIERS = ['HATCHBACK', 'SEDAN', 'SUV', 'LUXURY'];

const emptyForm = {
  regionalOffice: '',
  regionCode: '',
  currency: 'INR',
  tierRates: { HATCHBACK: 1500, SEDAN: 1600, SUV: 1700, LUXURY: 1800 },
  hoursPerDay: 12,
  roundMinutes: 30,
  chargeOvertime: true,
  hourlyFullDayAtHours: 6,
  minBillableHours: 0,
  overtimeGraceMinutes: 0,
  overtimeRateMultiplier: 1,
  maxExtraHours: 0,
  maxExtraDays: 0,
  freeReachKm: 5,
  reachRatePerKm: 8,
  roundTripExtra: 0,
  roundTripExtraType: 'AMOUNT',
  confirmationFeeAmount: 20,
  minFareMultiplier: 0.9,
  maxFareMultiplier: 1.1,
  isActive: true,
  // Duration packages. The form edits these as a few plain fields and converts
  // to the saved `packages` array on submit (see formToPackages), so a region with
  // packagesOn false saves an empty array and keeps the original Hourly / Full Day /
  // Multi-Day options in the app.
  packagesOn: false,
  // hours '' = automatic: half of hoursPerDay (12 -> 6). A number overrides it.
  halfDay: { enabled: true, label: 'Half Day', hours: '', ratePercent: 50 },
  fullDay: { enabled: true, label: 'Full Day' },
  defaultPackage: 'HALF_DAY',
  // Off by default: with packages on, the app shows the packages (and Multiple Days)
  // only. Tick it to also offer Hourly.
  hourlyEnabled: false,
  hourlyConvertToPackage: 'FULL_DAY',
  upgradeBufferPercent: 10,
  chargePackageUpgrade: true,
  // Whole-day billing: off until a region opts in. Times are minutes after midnight.
  wholeDayBilling: false,
  restStartMinutes: 1320, // 10 PM
  restEndMinutes: 360,    // 6 AM
  nextDayThresholdPercent: 50,
  // Food / Stay / one-way bus fare / custom charges (all off until switched on).
  allowances: defaultAllowances(),
};

// Minutes after midnight <-> the HH:MM a time input speaks.
const minutesToTime = (m) => {
  const n = Number(m);
  if (!Number.isFinite(n) || n < 0 || n >= 1440) return '';
  return `${String(Math.floor(n / 60)).padStart(2, '0')}:${String(n % 60).padStart(2, '0')}`;
};
const timeToMinutes = (t) => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(String(t || ''));
  if (!match) return '';
  return Number(match[1]) * 60 + Number(match[2]);
};
// "22:00" -> "10:00 PM", for the worked example.
const clock12 = (m) => {
  const n = Number(m);
  if (!Number.isFinite(n)) return '';
  const h24 = Math.floor(n / 60) % 24;
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${String(n % 60).padStart(2, '0')} ${h24 < 12 ? 'AM' : 'PM'}`;
};

// Saved config -> the form's flat package fields.
const packagesToForm = (editing) => {
  const packages = Array.isArray(editing?.packages) ? editing.packages : [];
  if (packages.length === 0) return {};
  const half = packages.find((p) => p.key === 'HALF_DAY');
  const full = packages.find((p) => p.key === 'FULL_DAY');
  const defaultEntry = packages.find((p) => p.enabled !== false && p.isDefault);
  return {
    packagesOn: true,
    halfDay: {
      ...emptyForm.halfDay,
      ...(half
        // A saved number equal to half the working day is the automatic value, so it
        // loads as automatic and keeps following hours per day.
        ? {
          enabled: half.enabled !== false,
          label: half.label || 'Half Day',
          hours: Number(half.hours) > 0 && Number(half.hours) !== (Number(editing.hoursPerDay) || 12) / 2 ? half.hours : '',
          ratePercent: half.ratePercent ?? 50,
        }
        : { enabled: false }),
    },
    fullDay: { enabled: full ? full.enabled !== false : true, label: full?.label || 'Full Day' },
    // 'HOURLY' when the booking screen is set to open on Hourly instead of a package.
    defaultPackage: editing.hourly?.isDefault === true && editing.hourly?.enabled !== false
      ? 'HOURLY'
      : (defaultEntry?.key || 'HALF_DAY'),
    hourlyEnabled: editing.hourly?.enabled !== false,
    hourlyConvertToPackage: editing.hourly?.convertToPackage || 'FULL_DAY',
  };
};

// The form's flat package fields -> the `packages` array the backend stores.
const formToPackages = (form, num) => {
  if (!form.packagesOn) return [];
  return [
    {
      key: 'HALF_DAY',
      label: form.halfDay.label || 'Half Day',
      // Left out when automatic, so the backend works it out as half of hours per day.
      ...(Number(form.halfDay.hours) > 0 ? { hours: Number(form.halfDay.hours) } : {}),
      // Priced as a % of the day rate. No per-tier prices are sent, so any saved
      // earlier are cleared and the percentage is the one rule.
      ratePercent: num(form.halfDay.ratePercent),
      enabled: !!form.halfDay.enabled,
      isDefault: form.defaultPackage === 'HALF_DAY',
      sortOrder: 1,
    },
    {
      key: 'FULL_DAY',
      label: form.fullDay.label || 'Full Day',
      hours: num(form.hoursPerDay),
      enabled: !!form.fullDay.enabled,
      isDefault: form.defaultPackage === 'FULL_DAY',
      sortOrder: 2,
    },
  ];
};

// ─── List ────────────────────────────────────────────────────────────────────
const List = ({ onEdit, onCreate, canWrite, canDelete }) => {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery(['acting-driver-fare'], () => api.get('/acting-driver-fare'));
  const configs = data?.data?.data?.configs || [];

  const del = useMutation((id) => api.delete(`/acting-driver-fare/${id}`), {
    onSuccess: () => { toast.success('Deleted'); qc.invalidateQueries(['acting-driver-fare']); },
    onError: (e) => toast.error(e?.response?.data?.message || 'Delete failed'),
  });
  const toggle = useMutation(({ id, isActive }) => api.patch(`/acting-driver-fare/${id}/status`, { isActive }), {
    onSuccess: () => { qc.invalidateQueries(['acting-driver-fare']); },
    onError: (e) => toast.error(e?.response?.data?.message || 'Update failed'),
  });

  if (isLoading) return <div className="card p-6">Loading…</div>;

  return (
    <div className="card">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {['Region', 'Office', 'Day Rates (H/S/SUV/L)', 'Hourly basis', 'Reach', 'Confirm Fee', 'Overtime', 'Status', ''].map((h) => (
                <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {configs.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">No acting-driver fare configs yet.</td></tr>
            )}
            {configs.map((c) => (
              <tr key={c._id}>
                <td className="px-4 py-3 font-medium text-gray-900">{c.regionCode}</td>
                <td className="px-4 py-3 text-gray-600">{c.regionalOffice?.name || '-'}</td>
                <td className="px-4 py-3 text-gray-600">
                  {TIERS.map((t) => c.tierRates?.[t] ?? '-').join(' / ')}
                </td>
                <td className="px-4 py-3 text-gray-600">/{c.hoursPerDay}h</td>
                <td className="px-4 py-3 text-gray-600">{c.freeReachKm}km free, ₹{c.reachRatePerKm}/km</td>
                <td className="px-4 py-3 text-gray-600">₹{c.confirmationFeeAmount ?? '-'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs ${c.chargeOvertime === false ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>
                    {c.chargeOvertime === false ? 'Not charged' : 'Charged'}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded-full text-xs ${c.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {c.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  {canWrite && (
                    <>
                      <button className="p-1.5 text-gray-500 hover:text-blue-600" title="Edit" onClick={() => onEdit(c)}><Edit2 className="h-4 w-4" /></button>
                      <button className="p-1.5 text-gray-500 hover:text-amber-600" title="Toggle" onClick={() => toggle.mutate({ id: c._id, isActive: !c.isActive })}><Power className="h-4 w-4" /></button>
                    </>
                  )}
                  {canDelete && (
                    <button className="p-1.5 text-gray-500 hover:text-red-600" title="Delete" onClick={() => { if (window.confirm('Delete this config?')) del.mutate(c._id); }}><Trash2 className="h-4 w-4" /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Section notes ────────────────────────────────────────────────────────────
// What each field actually does, in the place it is set. These rules are easy to
// set and hard to reason about later — a multiplier or a floor changes what every
// customer in the region pays, and the effect is not obvious from the label.
const SECTION_NOTES = {
  limits: [
    ['Hourly becomes a full day at',
      'The app\'s hourly picker stops here, and booking exactly this many hours is charged as ONE WHOLE DAY. The customer also receives the full day\'s hours, so overtime only starts once the day is used up — a 6h booking on a 12h day costs a full day and can run 12h before any overage.'],
    ['Minimum billable hours',
      'A floor for short bookings: anything shorter still bills this much. Use it when a very short job is not worth the driver\'s travel. It never raises a booking above one day, and 0 turns it off.'],
  ],
  overtime: [
    ['How overruns are billed',
      'An hourly or Full Day booking that runs long is billed BY THE HOUR. A booking of two or more days is billed by WHOLE DAYS — charging the clock there would bill the nights the driver is not working, turning a 6-day trip into 8 days of fare.'],
    ['Grace period',
      'Subtracted before rounding, so a driver 10 minutes late under a 15-minute grace costs nothing rather than rounding up to a whole billing increment. 0 = overtime starts the moment the booked time passes.'],
    ['Overtime rate multiplier',
      '1 = extra time bills at the plain hourly rate, which makes running over the cheapest way to buy more time. Above 1 charges a premium (1.5 = 1.5x). Applies to extra days too.'],
    ['Max extra hours / days',
      'Caps the overage, for when a car simply does not come back. 0 = uncapped. Hours apply to hourly and Full Day bookings, days to multi-day.'],
    ['Charge for extra hours / days',
      'The master switch. Off ignores everything above: the customer pays the quoted fare however long the trip ran.'],
  ],
  wholeDay: [
    ['What it does',
      'For a Full Day booking (or a Half Day that overran into a Full Day). Time after the booked day is counted in WORKING hours only — the rest window is left out — and once more than the threshold of another day is used, the whole extra day is billed instead of more hourly overtime.'],
    ['Rest hours',
      'A daily window (for example 10:00 PM to 6:00 AM). Hours inside it are not counted, so a driver who sleeps is not billed for the night. A same-day overrun that never reaches the window is still billed in full, hour by hour.'],
    ['Next-day threshold',
      'With 50% and a 12-hour day: up to 6 working hours past the booked day are billed hourly; more than 6 bills the whole next day. A longer trip then repeats the same step (a third day past 1.5 days of extra work, and so on).'],
    ['Existing trips',
      'The settings are frozen onto each trip when it is booked. Changing them here affects new bookings only. Needs "Charge for extra hours / days" on; and it applies to Full Day, not to hourly bookings under a day or to multi-day bookings (those bill by calendar day).'],
  ],
  range: [
    ['These do NOT change what is charged',
      'The multipliers only widen the range SHOWN to the customer before booking ("₹720 – ₹880"). The fare actually quoted and billed is the computed one. Setting them wider does not earn more.'],
    ['Min / Max Fare Multiplier',
      '0.9 and 1.1 show the estimate at -10% / +10%. Set both to 1 to show a single exact figure instead of a range.'],
  ],
};

const SectionHeading = ({ title, notesKey, openKey, setOpenKey }) => {
  const open = openKey === notesKey;
  return (
    <div>
      <div className="flex items-center gap-2">
        <h3 className="text-sm font-semibold text-gray-800">{title}</h3>
        <button
          type="button"
          onClick={() => setOpenKey(open ? null : notesKey)}
          className={`p-0.5 rounded-full transition-colors ${open ? 'text-blue-600 bg-blue-50' : 'text-gray-400 hover:text-blue-600'}`}
          title={open ? 'Hide notes' : 'What do these do?'}
          aria-expanded={open}
        >
          {open ? <X className="h-4 w-4" /> : <Info className="h-4 w-4" />}
        </button>
      </div>
      {open && (
        <div className="mt-2 mb-1 rounded-lg border border-blue-100 bg-blue-50/60 p-3 space-y-2">
          {(SECTION_NOTES[notesKey] || []).map(([label, text]) => (
            <div key={label}>
              <p className="text-xs font-semibold text-gray-700">{label}</p>
              <p className="text-xs text-gray-600 leading-relaxed">{text}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── Form (create / edit) ──────────────────────────────────────────────────────
const Form = ({ editing, onBack, onSaved }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState(() => {
    if (!editing) return emptyForm;
    return {
      ...emptyForm,
      ...editing,
      regionalOffice: editing.regionalOffice?._id || editing.regionalOffice || '',
      tierRates: { ...emptyForm.tierRates, ...(editing.tierRates || {}) },
      allowances: allowancesFromConfig(editing.allowances),
      ...packagesToForm(editing),
    };
  });

  // Only one notes panel open at a time — two expanded blocks push the fields
  // off screen and the form stops being a form.
  const [openNotes, setOpenNotes] = useState(null);

  const { data: officesRes } = useQuery(['adf-regional-offices'], () => api.get('/acting-driver-fare/regional-offices'));
  const offices = officesRes?.data?.data || [];

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTier = (t, v) => setForm((f) => ({ ...f, tierRates: { ...f.tierRates, [t]: v } }));

  const onOffice = (id) => {
    const office = offices.find((o) => o._id === id);
    setForm((f) => ({
      ...f,
      regionalOffice: id,
      regionCode: f.regionCode || (office?.name || '').replace(/\s+/g, '_').toUpperCase(),
    }));
  };

  const save = useMutation(
    (payload) => (editing ? api.put(`/acting-driver-fare/${editing._id}`, payload) : api.post('/acting-driver-fare', payload)),
    {
      onSuccess: () => { toast.success(editing ? 'Updated' : 'Created'); qc.invalidateQueries(['acting-driver-fare']); onSaved(); },
      onError: (e) => toast.error(e?.response?.data?.message || 'Save failed'),
    }
  );

  const submit = (e) => {
    e.preventDefault();
    // Office is optional (the region-wide 'default' fallback config has none).
    if (!form.regionCode) { toast.error('Region code is required'); return; }
    const num = (v) => (v === '' || v == null ? 0 : Number(v));
    save.mutate({
      ...form,
      hoursPerDay: num(form.hoursPerDay),
      roundMinutes: num(form.roundMinutes),
      freeReachKm: num(form.freeReachKm),
      reachRatePerKm: num(form.reachRatePerKm),
      roundTripExtra: num(form.roundTripExtra),
      roundTripExtraType: form.roundTripExtraType || 'AMOUNT',
      confirmationFeeAmount: num(form.confirmationFeeAmount),
      minFareMultiplier: num(form.minFareMultiplier),
      maxFareMultiplier: num(form.maxFareMultiplier),
      chargeOvertime: !!form.chargeOvertime,
      hourlyFullDayAtHours: num(form.hourlyFullDayAtHours),
      minBillableHours: num(form.minBillableHours),
      overtimeGraceMinutes: num(form.overtimeGraceMinutes),
      overtimeRateMultiplier: num(form.overtimeRateMultiplier),
      maxExtraHours: num(form.maxExtraHours),
      maxExtraDays: num(form.maxExtraDays),
      tierRates: Object.fromEntries(TIERS.map((t) => [t, num(form.tierRates[t])])),
      packages: formToPackages(form, num),
      // convertAtHours is left unset on purpose: "Hourly becomes a full day at"
      // (hourlyFullDayAtHours) stays the one place that threshold is edited.
      hourly: {
        enabled: !!form.hourlyEnabled,
        convertToPackage: form.hourlyConvertToPackage || 'FULL_DAY',
        // The app opens on Hourly only when the admin picked it as the default.
        isDefault: form.packagesOn && form.defaultPackage === 'HOURLY' && !!form.hourlyEnabled,
      },
      upgradeBufferPercent: num(form.upgradeBufferPercent),
      chargePackageUpgrade: form.chargePackageUpgrade !== false,
      wholeDayBilling: !!form.wholeDayBilling,
      // A blank time input is '' — keep the stored default rather than saving 0 (midnight).
      restStartMinutes: form.restStartMinutes === '' ? 1320 : num(form.restStartMinutes),
      restEndMinutes: form.restEndMinutes === '' ? 360 : num(form.restEndMinutes),
      nextDayThresholdPercent: num(form.nextDayThresholdPercent) || 50,
      allowances: allowancesToPayload(form.allowances),
    });
  };

  const setHalf = (k, v) => setForm((f) => ({ ...f, halfDay: { ...f.halfDay, [k]: v } }));
  const setFull = (k, v) => setForm((f) => ({ ...f, fullDay: { ...f.fullDay, [k]: v } }));

  const numField = (label, key, step = '1', hint) => (
    <div>
      <label className="form-label">{label}</label>
      <input type="number" step={step} className="input" value={form[key]} onChange={(e) => set(key, e.target.value)} />
      {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-6">
      <div className="card p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-800">Region</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="form-label">Regional Office</label>
            <select className="input" value={form.regionalOffice} onChange={(e) => onOffice(e.target.value)}>
              <option value="">None (region-wide / default)</option>
              {offices.map((o) => <option key={o._id} value={o._id}>{o.name}</option>)}
            </select>
          </div>
          <div>
            <label className="form-label">Region Code *</label>
            <input className="input" value={form.regionCode} onChange={(e) => set('regionCode', e.target.value)} placeholder="AUTO_FROM_OFFICE" />
          </div>
          <div>
            <label className="form-label">Currency</label>
            <select className="input" value={form.currency} onChange={(e) => set('currency', e.target.value)}>
              {['INR', 'USD', 'EUR', 'GBP'].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
      </div>

      <div className="card p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-800">Day Rates by Tier (hourly = day rate ÷ hours/day)</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {TIERS.map((t) => (
            <div key={t}>
              <label className="form-label">{t}</label>
              <input type="number" step="1" className="input" value={form.tierRates[t]} onChange={(e) => setTier(t, e.target.value)} />
              <p className="text-xs text-gray-400 mt-1">≈ ₹{form.hoursPerDay > 0 ? Math.round(Number(form.tierRates[t] || 0) / Number(form.hoursPerDay)) : 0}/hr</p>
            </div>
          ))}
        </div>
      </div>

      <div className="card p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">Duration Packages</h3>
            <p className="text-xs text-gray-400 mt-0.5">
              Off: customers see the original Hourly / Full Day / Multi-Day options. On: they pick
              from the packages below, with the default pre-selected.
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm whitespace-nowrap">
            <input
              type="checkbox"
              checked={!!form.packagesOn}
              onChange={(e) => {
                const on = e.target.checked;
                // Turning packages on starts with Hourly hidden, so the app shows
                // Half Day / Full Day only. It can be ticked back on below.
                setForm((f) => ({
                  ...f,
                  packagesOn: on,
                  ...(on && !f.packagesOn ? { hourlyEnabled: false, defaultPackage: f.defaultPackage === 'HOURLY' ? 'HALF_DAY' : f.defaultPackage } : {}),
                }));
              }}
            />
            Use packages
          </label>
        </div>

        {form.packagesOn && (
          <>
            <div className="rounded-lg border border-gray-200 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={!!form.halfDay.enabled} onChange={(e) => setHalf('enabled', e.target.checked)} />
                  Half Day
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="radio"
                    name="defaultPackage"
                    checked={form.defaultPackage === 'HALF_DAY'}
                    disabled={!form.halfDay.enabled}
                    onChange={() => set('defaultPackage', 'HALF_DAY')}
                  />
                  Default
                </label>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Name shown in app</label>
                  <input className="input" value={form.halfDay.label} onChange={(e) => setHalf('label', e.target.value)} />
                </div>
                <div>
                  <label className="form-label">Hours</label>
                  <input
                    type="number"
                    step="0.5"
                    className="input"
                    value={form.halfDay.hours}
                    placeholder={String((Number(form.hoursPerDay) || 12) / 2)}
                    onChange={(e) => setHalf('hours', e.target.value)}
                  />
                  <p className="text-xs text-gray-400 mt-1">
                    {form.halfDay.hours === '' || form.halfDay.hours == null
                      ? `Automatic: half of hours per day = ${(Number(form.hoursPerDay) || 12) / 2}h. Type a number to change it.`
                      : `Custom (below ${form.hoursPerDay}h). `}
                    {form.halfDay.hours !== '' && form.halfDay.hours != null && (
                      <button type="button" className="underline" onClick={() => setHalf('hours', '')}>
                        Use automatic ({(Number(form.hoursPerDay) || 12) / 2}h)
                      </button>
                    )}
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Price (% of the day rate)</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    max="99"
                    className="input"
                    value={form.halfDay.ratePercent}
                    onChange={(e) => setHalf('ratePercent', e.target.value)}
                  />
                  <p className="text-xs text-gray-400 mt-1">50 = half the day rate. Must be between 1 and 99.</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {TIERS.map((t) => {
                  const dayRate = Number(form.tierRates[t]) || 0;
                  const pct = Number(form.halfDay.ratePercent) || 0;
                  const price = Math.round(dayRate * (pct / 100));
                  return (
                    <div key={t} className="rounded-md bg-gray-50 px-3 py-2">
                      <p className="text-xs text-gray-500">{t}</p>
                      <p className="text-sm font-semibold text-gray-800">{price > 0 ? `₹${price}` : '—'}</p>
                      <p className="text-xs text-gray-400">of ₹{dayRate} day rate</p>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-400">
                The Half Day price follows each vehicle&apos;s day rate above, so changing a day rate moves it too.
                It is a flat price for the {Number(form.halfDay.hours) > 0 ? Number(form.halfDay.hours) : (Number(form.hoursPerDay) || 12) / 2} hours, not hours × a rate.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input type="checkbox" checked={!!form.fullDay.enabled} onChange={(e) => setFull('enabled', e.target.checked)} />
                  Full Day
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="radio"
                    name="defaultPackage"
                    checked={form.defaultPackage === 'FULL_DAY'}
                    disabled={!form.fullDay.enabled}
                    onChange={() => set('defaultPackage', 'FULL_DAY')}
                  />
                  Default
                </label>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">Name shown in app</label>
                  <input className="input" value={form.fullDay.label} onChange={(e) => setFull('label', e.target.value)} />
                </div>
              </div>
              <p className="text-xs text-gray-400">
                Priced from the day rates above and lasts {form.hoursPerDay} hours. Multi-Day is always
                available as a number of Full Days.
              </p>
            </div>

            <div className="rounded-lg border border-gray-200 p-4 space-y-3">
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="checkbox"
                    checked={!!form.hourlyEnabled}
                    onChange={(e) => {
                      set('hourlyEnabled', e.target.checked);
                      // Hourly cannot stay the default once it is switched off.
                      if (!e.target.checked && form.defaultPackage === 'HOURLY') set('defaultPackage', form.halfDay.enabled ? 'HALF_DAY' : 'FULL_DAY');
                    }}
                  />
                  Allow hourly bookings
                  <span className="block text-xs font-normal text-gray-400">
                    Off: the app shows the packages and Multiple Days only, no Hourly option.
                  </span>
                </label>
                <label className="flex items-center gap-2 text-xs text-gray-600">
                  <input
                    type="radio"
                    name="defaultPackage"
                    checked={form.defaultPackage === 'HOURLY'}
                    disabled={!form.hourlyEnabled}
                    onChange={() => set('defaultPackage', 'HOURLY')}
                  />
                  Default
                </label>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div>
                  <label className="form-label">At or above {Number(form.hourlyFullDayAtHours) || 0}h, bill as</label>
                  <select
                    className="input"
                    value={form.hourlyConvertToPackage}
                    disabled={!form.hourlyEnabled}
                    onChange={(e) => set('hourlyConvertToPackage', e.target.value)}
                  >
                    <option value="FULL_DAY">Full Day</option>
                    <option value="HALF_DAY">Half Day</option>
                  </select>
                  <p className="text-xs text-gray-400 mt-1">
                    The hour threshold is &quot;Hourly becomes a full day at&quot; under Booking Limits. Below it, the customer pays
                    the hours booked at the hourly rate.
                  </p>
                </div>
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 p-4 space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {numField('Route-time buffer (%)', 'upgradeBufferPercent', '1',
                  'A route this much over a package still counts as too long, so a longer one is suggested')}
              </div>
              <label className="flex items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={form.chargePackageUpgrade !== false}
                  onChange={(e) => set('chargePackageUpgrade', e.target.checked)}
                />
                <span>
                  Bill a Half Day that runs over as a Full Day
                  <span className="block text-xs text-gray-400 mt-0.5">
                    On: past the Half Day hours the customer pays the Full Day price, then overtime after
                    {' '}{form.hoursPerDay} hours. Off: the Half Day price stands (overtime below still applies).
                  </span>
                </span>
              </label>
            </div>
          </>
        )}
      </div>

      <div className="card p-6 space-y-4">
        <h3 className="text-sm font-semibold text-gray-800">Time & Reach</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {numField('Hours per Day', 'hoursPerDay', '0.5', 'Hourly rate = day rate ÷ this')}
          {numField('Round up to (minutes)', 'roundMinutes', '1', 'Booked/actual hours rounded up')}
          {numField('Free Reach (km)', 'freeReachKm', '0.5', 'Driver→pickup free allowance')}
          {numField('Reach Rate (₹/km)', 'reachRatePerKm', '0.5', 'Beyond the free km')}
          <div>
            <label className="form-label">Round Trip Extra</label>
            <div className="flex gap-2">
              <input
                type="number"
                step="1"
                min="0"
                className="input"
                value={form.roundTripExtra}
                onChange={(e) => set('roundTripExtra', e.target.value)}
              />
              <select
                className="input w-28"
                value={form.roundTripExtraType || 'AMOUNT'}
                onChange={(e) => set('roundTripExtraType', e.target.value)}
              >
                <option value="AMOUNT">₹ flat</option>
                <option value="PERCENT">% of fare</option>
              </select>
            </div>
            <p className="text-xs text-gray-400 mt-1">
              {form.roundTripExtraType === 'PERCENT'
                ? `${Number(form.roundTripExtra) || 0}% of the service fare — a longer booking pays proportionally more`
                : `Flat ₹${Number(form.roundTripExtra) || 0} whatever the booking length`}
              . 0 = none.
            </p>
          </div>
          {numField('Confirmation Fee (₹)', 'confirmationFeeAmount', '1', 'Booking advance shown on Confirm page; cash → driver due (0 = none)')}
        </div>
        <div className="pt-2 border-t border-gray-100">
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={form.chargeOvertime !== false}
              onChange={(e) => set('chargeOvertime', e.target.checked)}
            />
            <span>
              Charge for extra hours / days
              <span className="block text-xs text-gray-400 mt-0.5">
                On: a trip that runs past its booked time is billed for the overage —
                by the hour for hourly and single-day bookings, by whole days for
                multi-day. Off: the customer pays the quoted fare however long it ran.
              </span>
            </span>
          </label>
        </div>
      </div>

      <div className="card p-6 space-y-4">
        <SectionHeading title="Booking Limits" notesKey="limits" openKey={openNotes} setOpenKey={setOpenNotes} />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {numField('Hourly becomes a full day at', 'hourlyFullDayAtHours', '1',
            'The app offers hourly up to this; booking it is charged as one day')}
          {numField('Minimum billable hours', 'minBillableHours', '0.5',
            '0 = no floor. A shorter booking still bills this much')}
        </div>

        <div className="pt-2">
          <SectionHeading title="Overtime" notesKey="overtime" openKey={openNotes} setOpenKey={setOpenNotes} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {numField('Grace period (minutes)', 'overtimeGraceMinutes', '5',
            '0 = none. Late by less than this is free')}
          {numField('Overtime rate multiplier', 'overtimeRateMultiplier', '0.05',
            '1 = plain hourly rate. 1.5 = extra time at 1.5x')}
          {numField('Max extra hours', 'maxExtraHours', '0.5',
            '0 = uncapped. Applies to hourly and full-day bookings')}
          {numField('Max extra days', 'maxExtraDays', '1',
            '0 = uncapped. Applies to multi-day bookings')}
        </div>
        <p className="text-xs text-gray-400">
          Every value here defaults to a no-op — leaving them as they are prices
          exactly as before. They are ignored entirely when &quot;Charge for extra
          hours / days&quot; is off.
        </p>
      </div>

      <div className="card p-6 space-y-4">
        <SectionHeading title="Long trips: whole-day billing" notesKey="wholeDay" openKey={openNotes} setOpenKey={setOpenNotes} />

        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={!!form.wholeDayBilling}
            disabled={form.chargeOvertime === false}
            onChange={(e) => set('wholeDayBilling', e.target.checked)}
          />
          <span>
            Bill whole days when a Full Day trip runs on
            <span className="block text-xs text-gray-400 mt-0.5">
              {form.chargeOvertime === false
                ? 'Turn on "Charge for extra hours / days" first — this works on top of it.'
                : 'Off: extra time is billed by the hour, the nights included, exactly as before.'}
            </span>
          </span>
        </label>

        {form.wholeDayBilling && form.chargeOvertime !== false && (
          <div className="rounded-lg border border-gray-200 p-4 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div>
                <label className="form-label">Rest starts</label>
                <input
                  type="time"
                  className="input"
                  value={minutesToTime(form.restStartMinutes)}
                  onChange={(e) => set('restStartMinutes', timeToMinutes(e.target.value))}
                />
                <p className="text-xs text-gray-400 mt-1">Driver is off from this time</p>
              </div>
              <div>
                <label className="form-label">Rest ends</label>
                <input
                  type="time"
                  className="input"
                  value={minutesToTime(form.restEndMinutes)}
                  onChange={(e) => set('restEndMinutes', timeToMinutes(e.target.value))}
                />
                <p className="text-xs text-gray-400 mt-1">Next morning (may be after midnight)</p>
              </div>
              {numField('Next day billed after (%)', 'nextDayThresholdPercent', '5',
                `${Number(form.nextDayThresholdPercent) || 50}% of ${Number(form.hoursPerDay) || 12} h = ${Math.round(((Number(form.nextDayThresholdPercent) || 50) / 100) * (Number(form.hoursPerDay) || 12) * 10) / 10} working hours`)}
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">
              Example: a Full Day picked up at 7:00 AM. If it runs on through the night, the hours between{' '}
              <strong>{clock12(form.restStartMinutes)}</strong> and <strong>{clock12(form.restEndMinutes)}</strong> are not
              counted. Once more than{' '}
              <strong>{Math.round(((Number(form.nextDayThresholdPercent) || 50) / 100) * (Number(form.hoursPerDay) || 12) * 10) / 10} working hours</strong>{' '}
              of another day are used, that whole day is billed at the Full Day price.
            </p>
          </div>
        )}
      </div>

      <AllowancesSection value={form.allowances} onChange={(v) => set('allowances', v)} />

      <div className="card p-6 space-y-4">
        <SectionHeading title="Estimate Range (customer-facing)" notesKey="range" openKey={openNotes} setOpenKey={setOpenNotes} />
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {numField('Min Fare Multiplier', 'minFareMultiplier', '0.05', 'e.g. 0.9 = -10%')}
          {numField('Max Fare Multiplier', 'maxFareMultiplier', '0.05', 'e.g. 1.1 = +10%')}
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.isActive} onChange={(e) => set('isActive', e.target.checked)} />
              Active
            </label>
          </div>
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button type="button" className="btn btn-secondary" onClick={onBack}>Cancel</button>
        <button type="submit" className="btn btn-primary" disabled={save.isLoading}>{save.isLoading ? 'Saving…' : (editing ? 'Update' : 'Create')}</button>
      </div>
    </form>
  );
};


// ─── Simulator ────────────────────────────────────────────────────────────────
// Prices a hypothetical trip through the REAL fare service (proxied to the rides
// backend), so what it shows is what a customer would actually be charged.
const MODES = [
  { value: 'HOURLY', label: 'Hourly' },
  // Only prices when the region has "Use packages" on and Half Day enabled.
  { value: 'HALF_DAY', label: 'Half Day' },
  { value: 'FULL_DAY', label: 'Full Day' },
  { value: 'MULTI_DAY', label: 'Multi-Day' },
];

// Dates are handled as plain 'YYYY-MM-DD' strings — the same shape the app sends
// as durationRangeStart/End — so no timezone shifts creep in between the picker
// and the fare service.
const DAY_MS = 86400000;
const isoDay = (d) => d.toISOString().slice(0, 10);
const addDays = (iso, n) => isoDay(new Date(Date.parse(iso + 'T00:00:00Z') + (n * DAY_MS)));
const daysBetween = (a, b) => Math.round((Date.parse(b + 'T00:00:00Z') - Date.parse(a + 'T00:00:00Z')) / DAY_MS) + 1;
const prettyDay = (iso) => {
  if (!iso) return '';
  const d = new Date(Date.parse(iso + 'T00:00:00Z'));
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'UTC' });
};

const Money = ({ v, currency = 'INR' }) => (
  <span>{currency === 'INR' ? '₹' : currency + ' '}{Number(v || 0).toLocaleString('en-IN')}</span>
);

const Row = ({ label, children, strong, muted }) => (
  <div className={`flex items-baseline justify-between py-2 ${strong ? 'border-t border-gray-300 mt-1 pt-3' : 'border-b border-gray-100'}`}>
    <span className={`text-sm ${muted ? 'text-gray-400' : 'text-gray-600'} ${strong ? 'font-semibold text-gray-800' : ''}`}>{label}</span>
    <span className={`text-sm tabular-nums ${strong ? 'text-lg font-bold text-gray-900' : 'font-medium text-gray-800'}`}>{children}</span>
  </div>
);

const Simulator = ({ configs }) => {
  const [form, setForm] = useState({
    regionCode: '',
    vehicleType: 'SEDAN',
    mode: 'HOURLY',
    hours: 4,
    actualHours: '',
    rangeStart: isoDay(new Date()),
    rangeEnd: addDays(isoDay(new Date()), 2),
    actualEnd: '',
    reachDistanceKm: 0,
    // Minutes to the drop-off. Blank = no destination, so no package suggestion.
    routeMinutes: '',
    isRoundTrip: false,
    isSubscriber: false,
    discountPercent: 50,
    billsTotal: 0,
    confirmationInCash: false,
  });
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Default to the first region so the page is usable the moment it opens.
  React.useEffect(() => {
    if (!form.regionCode && configs.length) {
      setForm((f) => ({ ...f, regionCode: configs[0].regionCode }));
    }
  }, [configs, form.regionCode]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const run = useMutation((payload) => api.post('/acting-driver-fare/simulate', payload), {
    onSuccess: (res) => {
      const body = res?.data;
      if (body?.success) { setResult(body.data); setError(''); }
      else { setResult(null); setError(body?.message || 'Simulation failed'); }
    },
    onError: (e) => {
      setResult(null);
      setError(e?.response?.data?.message || e.message || 'Simulation failed');
    },
  });

  const submit = (e) => {
    e.preventDefault();
    if (!form.regionCode) { toast.error('Pick a region'); return; }
    const n = (v) => (v === '' || v == null ? undefined : Number(v));
    run.mutate({
      regionCode: form.regionCode,
      vehicleType: form.vehicleType,
      mode: form.mode,
      hours: n(form.hours),
      actualHours: n(form.actualHours),
      rangeStart: form.rangeStart,
      rangeEnd: form.rangeEnd,
      // Blank = came back on time; the backend then bills no extra days.
      actualEnd: form.actualEnd || form.rangeEnd,
      reachDistanceKm: n(form.reachDistanceKm) || 0,
      routeMinutes: n(form.routeMinutes) || 0,
      isRoundTrip: form.isRoundTrip,
      isSubscriber: form.isSubscriber,
      discountPercent: n(form.discountPercent),
      billsTotal: n(form.billsTotal) || 0,
      confirmationInCash: form.confirmationInCash,
    });
  };

  const cur = result?.config?.currency || 'INR';
  const isMulti = form.mode === 'MULTI_DAY';

  // Live feedback under the pickers, so the effect of a date is visible before
  // pressing Calculate.
  const bookedDayCount = (form.rangeStart && form.rangeEnd && form.rangeEnd >= form.rangeStart)
    ? daysBetween(form.rangeStart, form.rangeEnd) : 0;
  const returnedOn = form.actualEnd || form.rangeEnd;
  const lateDays = (bookedDayCount > 0 && returnedOn > form.rangeEnd)
    ? daysBetween(form.rangeStart, returnedOn) - bookedDayCount : 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* ── Inputs ── */}
      <form onSubmit={submit} className="card p-6 space-y-5">
        <h3 className="text-sm font-semibold text-gray-800">The booking</h3>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="form-label">Region</label>
            <select className="input" value={form.regionCode} onChange={(e) => set('regionCode', e.target.value)}>
              {configs.length === 0 && <option value="">No configs yet</option>}
              {configs.map((c) => (
                <option key={c._id} value={c.regionCode}>
                  {c.regionCode}{c.isActive ? '' : ' (inactive)'}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="form-label">Vehicle</label>
            <select className="input" value={form.vehicleType} onChange={(e) => set('vehicleType', e.target.value)}>
              {TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="form-label">Duration</label>
          <div className="flex gap-2">
            {MODES.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => set('mode', m.value)}
                className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                  form.mode === m.value
                    ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                    : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {form.mode === 'HOURLY' && (
          <div>
            <label className="form-label">Hours booked</label>
            <input
              type="number"
              min="1"
              max={result?.config?.hourlyFullDayAtHours ?? 6}
              className="input"
              value={form.hours}
              onChange={(e) => set('hours', e.target.value)}
            />
            <p className="text-xs text-gray-400 mt-1">
              The app offers 1–{result?.config?.hourlyFullDayAtHours ?? 6}h. At{' '}
              {result?.config?.hourlyFullDayAtHours ?? 6}h it is charged as a full day.
            </p>
          </div>
        )}
        {isMulti && (
          <div>
            <label className="form-label">Dates booked</label>
            <div className="flex items-center gap-2">
              <input
                type="date"
                className="input"
                value={form.rangeStart}
                onChange={(e) => {
                  const start = e.target.value;
                  // Keep the range valid: an end before the start prices as nothing.
                  setForm((f) => ({
                    ...f,
                    rangeStart: start,
                    rangeEnd: f.rangeEnd && f.rangeEnd >= start ? f.rangeEnd : addDays(start, 2),
                  }));
                }}
              />
              <span className="text-gray-400 text-sm">→</span>
              <input
                type="date"
                className="input"
                min={form.rangeStart}
                value={form.rangeEnd}
                onChange={(e) => set('rangeEnd', e.target.value)}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              {bookedDayCount > 0
                ? `${bookedDayCount} day${bookedDayCount === 1 ? '' : 's'} — both dates included, exactly as the app counts them.`
                : 'End date must be on or after the start date.'}
            </p>
          </div>
        )}

        <h3 className="text-sm font-semibold text-gray-800 pt-2">What actually happened</h3>
        <div className="grid grid-cols-2 gap-4">
          {isMulti ? (
            <div>
              <label className="form-label">Car actually returned on</label>
              <input
                type="date"
                className="input"
                min={form.rangeStart}
                value={form.actualEnd || form.rangeEnd}
                onChange={(e) => set('actualEnd', e.target.value)}
              />
              <p className="text-xs text-gray-500 mt-1">
                {lateDays > 0
                  ? `${lateDays} day${lateDays === 1 ? '' : 's'} late — billed as whole extra days.`
                  : 'Back on time — no overtime.'}
              </p>
            </div>
          ) : (
            <div>
              <label className="form-label">Hours actually taken</label>
              <input type="number" min="0" step="0.5" className="input" placeholder="on time" value={form.actualHours} onChange={(e) => set('actualHours', e.target.value)} />
              <p className="text-xs text-gray-400 mt-1">Overrun bills by the hour.</p>
            </div>
          )}
          <div>
            <label className="form-label">Reach distance (km)</label>
            <input type="number" min="0" step="0.5" className="input" value={form.reachDistanceKm} onChange={(e) => set('reachDistanceKm', e.target.value)} />
            <p className="text-xs text-gray-400 mt-1">Driver → pickup.</p>
          </div>
          <div>
            <label className="form-label">Route time to drop-off (min)</label>
            <input type="number" min="0" step="5" className="input" placeholder="no destination" value={form.routeMinutes} onChange={(e) => set('routeMinutes', e.target.value)} />
            <p className="text-xs text-gray-400 mt-1">If longer than the booking, the app suggests a longer package.</p>
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input type="checkbox" checked={form.isRoundTrip} onChange={(e) => set('isRoundTrip', e.target.checked)} />
          Round trip (adds the region surcharge)
        </label>

        <h3 className="text-sm font-semibold text-gray-800 pt-2">Customer &amp; expenses</h3>

        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.isSubscriber} onChange={(e) => set('isSubscriber', e.target.checked)} />
            NOT Premium subscriber
          </label>
          {form.isSubscriber && (
            <div className="pl-6">
              <label className="form-label">Discount %</label>
              <input type="number" min="0" max="100" className="input" value={form.discountPercent} onChange={(e) => set('discountPercent', e.target.value)} />
              <p className="text-xs text-gray-400 mt-1">
                The plan is 50%. Applies to our own (in-house) drivers only, and spends
                one of the subscriber&apos;s 3 discounted trips per cycle.
              </p>
            </div>
          )}

          <div>
            <label className="form-label">Approved expenses (₹)</label>
            <input type="number" min="0" step="1" className="input" value={form.billsTotal} onChange={(e) => set('billsTotal', e.target.value)} />
            <p className="text-xs text-gray-400 mt-1">
              Tolls, parking, fuel the driver paid. Reimbursed in full — never discounted.
              Pending bills are excluded until approved.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={form.confirmationInCash} onChange={(e) => set('confirmationInCash', e.target.checked)} />
            Confirmation fee collected in cash
          </label>
        </div>

        <button type="submit" className="btn btn-primary w-full" disabled={run.isLoading}>
          {run.isLoading ? 'Calculating…' : 'Calculate fare'}
        </button>
      </form>

      {/* ── Result ── */}
      <div className="space-y-4">
        {error && (
          <div className="card p-4 border-l-4 border-red-400 bg-red-50">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {!result && !error && (
          <div className="card p-6 text-sm text-gray-500 space-y-3">
            <p className="text-gray-600">
              Describe a booking on the left and press <strong>Calculate fare</strong>.
              The numbers come from the live fare service, so they are what a real
              trip would be charged.
            </p>
            <div className="border-t border-gray-100 pt-3 space-y-2 text-xs">
              <p><strong className="text-gray-700">Hourly</strong> — 1 to 6 hours. Booking the
                6h cap is charged as a full day, so it gets a full day's hours too:
                overtime only starts after the day is used up.</p>
              <p><strong className="text-gray-700">Half Day</strong> — a flat price (a % of the
                day rate) for its hours. Running past them bills a Full Day, then overtime.</p>
              <p><strong className="text-gray-700">Full Day</strong> — one working day.
                Running past it is billed by the hour.</p>
              <p><strong className="text-gray-700">Multi-Day</strong> — pick the booked dates,
                then the date the car actually came back. Coming back late is billed
                as whole extra days, never as clock hours, so the nights the driver
                isn't working are not charged.</p>
              <p><strong className="text-gray-700">Customer pays</strong> — the NOT Premium
                discount comes off the fare (service + reach + overtime); approved
                expenses are reimbursed on top and are never discounted.</p>
            </div>
          </div>
        )}

        {result?.config?.rulesSupported === false && (
          <div className="card p-4 border-l-4 border-amber-400 bg-amber-50">
            <p className="text-sm text-amber-800">
              The rides backend is running an older fare service, so these numbers
              ignore the Booking Limits and Overtime rules. Redeploy
              <code className="mx-1 px-1 bg-amber-100 rounded text-xs">fareEngine/services/ActingDriverFareService.js</code>
              and restart it.
            </p>
          </div>
        )}

        {result && (
          <>
            <div className="card p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">Quoted at booking</h3>
              <Row label={result.booking.rangeStart
                ? `Booked · ${prettyDay(result.booking.rangeStart)} → ${prettyDay(result.booking.rangeEnd)} (${result.booking.bookedLabel})`
                : 'Booked · ' + result.booking.bookedLabel}>
                {result.booking.bookedHours} billable hrs
              </Row>
              <Row label="Service fare"><Money v={result.estimate.serviceFare} currency={cur} /></Row>
              {result.estimate.roundTripExtra > 0 && (
                <Row label={result.config.roundTripExtraType === 'PERCENT'
                  ? `Round trip extra (${result.config.roundTripExtraValue}%)`
                  : 'Round trip extra'}>
                  <Money v={result.estimate.roundTripExtra} currency={cur} />
                </Row>
              )}
              <Row label="Shown as a range" muted>
                <Money v={result.estimate.minFare} currency={cur} /> – <Money v={result.estimate.maxFare} currency={cur} />
              </Row>
              {result.estimate.confirmationFee > 0 && (
                <Row label="Confirmation fee (paid at booking)"><Money v={result.estimate.confirmationFee} currency={cur} /></Row>
              )}
              <Row label="Quoted total" strong><Money v={result.estimate.quotedFare} currency={cur} /></Row>
            </div>

            <div className="card p-5">
              <h3 className="text-sm font-semibold text-gray-800 mb-2">
                Charged at trip end · ran {result.actual.actualLabel}
              </h3>
              <Row label="Booked portion">{result.booking.bookedHours} hrs</Row>
              {result.actual.upgraded && (
                <Row label="Package upgrade (ran past the booked package)">
                  + <Money v={result.actual.upgradeFare} currency={cur} />
                </Row>
              )}
              {result.actual.overtimeApplies && (result.actual.extraHours > 0 || result.actual.extraDays > 0) ? (
                <Row label={result.actual.extraDays > 0
                  ? 'Extra days (' + result.actual.extraDays + ' × ' + result.config.hoursPerDay + 'h)'
                  : 'Extra hours (' + result.actual.extraHours + 'h)'}>
                  + <Money v={result.actual.extraFare - (result.actual.upgradeFare || 0)} currency={cur} />
                </Row>
              ) : result.actual.upgraded ? null : (
                <Row label="Overtime" muted>
                  {result.config.chargeOvertime === false ? 'not charged in this region' : 'none'}
                </Row>
              )}
              {result.actual.reachFee > 0 && (
                <Row label={'Reach (' + result.actual.reachDistanceKm + 'km, ' + result.config.freeReachKm + 'km free)'}>
                  + <Money v={result.actual.reachFee} currency={cur} />
                </Row>
              )}
              <Row label="Fare" strong><Money v={result.actual.finalFare} currency={cur} /></Row>
              {result.actual.finalFare !== result.estimate.quotedFare && (
                <p className="text-xs text-amber-600 mt-2">
                  Differs from the quote by{' '}
                  <Money v={Math.abs(result.actual.finalFare - result.estimate.quotedFare)} currency={cur} />
                  {result.actual.finalFare > result.estimate.quotedFare ? ' more' : ' less'}.
                </p>
              )}
            </div>

            {result.suggestion && (
              <div className="card p-4 border-l-4 border-amber-400 bg-amber-50">
                <p className="text-sm text-amber-800">
                  The app would suggest <strong>{result.suggestion.label}</strong> (<Money v={result.suggestion.fare} currency={cur} />)
                  for this route. The customer can keep their choice; extra time is then billed as above.
                </p>
              </div>
            )}

            {result.invoice && (
              <div className="card p-5 border-l-4 border-green-500">
                <h3 className="text-sm font-semibold text-gray-800 mb-2">Customer pays</h3>
                <Row label="Fare (service + reach + overtime)">
                  <Money v={result.invoice.earningBeforeDiscount} currency={cur} />
                </Row>
                {result.invoice.discountAmount > 0 && (
                  <Row label={`NOT Premium discount (${result.invoice.discountPercent}%)`}>
                    <span className="text-green-600">− <Money v={result.invoice.discountAmount} currency={cur} /></span>
                  </Row>
                )}
                {result.invoice.billsTotal > 0 && (
                  <Row label="Approved expenses (not discounted)">
                    + <Money v={result.invoice.billsTotal} currency={cur} />
                  </Row>
                )}
                {result.invoice.confirmationCashDue > 0 && (
                  <Row label="Confirmation fee (collected in cash)">
                    + <Money v={result.invoice.confirmationCashDue} currency={cur} />
                  </Row>
                )}
                <Row label="Total payable" strong>
                  <Money v={result.invoice.totalPayable} currency={cur} />
                </Row>
                {result.invoice.isSubscriber && result.invoice.discountAmount > 0 && (
                  <p className="text-xs text-gray-400 mt-2">
                    Discount applies only when the trip runs with one of our own drivers,
                    and uses one of the subscriber&apos;s 3 slots for the cycle.
                  </p>
                )}
              </div>
            )}

            <div className="card p-5 bg-gray-50">
              <h3 className="text-xs font-semibold text-gray-500 uppercase mb-3">Rates used</h3>
              <div className="grid grid-cols-2 gap-x-6 text-xs text-gray-600">
                <div className="flex justify-between py-1"><span>Region</span><span className="font-medium">{result.config.regionCode}</span></div>
                <div className="flex justify-between py-1"><span>Vehicle</span><span className="font-medium">{result.booking.vehicleType}</span></div>
                <div className="flex justify-between py-1"><span>Per day</span><span className="font-medium"><Money v={result.config.ratePerDay} currency={cur} /></span></div>
                <div className="flex justify-between py-1"><span>Per hour</span><span className="font-medium"><Money v={result.config.ratePerHour} currency={cur} /></span></div>
                <div className="flex justify-between py-1"><span>Hours per day</span><span className="font-medium">{result.config.hoursPerDay}h</span></div>
                <div className="flex justify-between py-1"><span>Rounding</span><span className="font-medium">{result.config.roundMinutes} min</span></div>
                <div className="flex justify-between py-1"><span>Overtime</span><span className="font-medium">{result.config.chargeOvertime === false ? 'off' : 'charged'}</span></div>
                <div className="flex justify-between py-1"><span>Full day at</span><span className="font-medium">{result.config.hourlyFullDayAtHours}h</span></div>
                {/* Only the rules that are actually doing something — a wall of
                    "0 / none / 1x" reads as configuration when it is the absence
                    of it. */}
                {result.config.minBillableHours > 0 && (
                  <div className="flex justify-between py-1"><span>Min billable</span><span className="font-medium">{result.config.minBillableHours}h</span></div>
                )}
                {result.config.overtimeGraceMinutes > 0 && (
                  <div className="flex justify-between py-1"><span>Grace</span><span className="font-medium">{result.config.overtimeGraceMinutes} min</span></div>
                )}
                {result.config.overtimeRateMultiplier !== 1 && (
                  <div className="flex justify-between py-1"><span>Overtime rate</span><span className="font-medium">{result.config.overtimeRateMultiplier}×</span></div>
                )}
                {result.config.maxExtraHours > 0 && (
                  <div className="flex justify-between py-1"><span>Max extra hrs</span><span className="font-medium">{result.config.maxExtraHours}h</span></div>
                )}
                {result.config.maxExtraDays > 0 && (
                  <div className="flex justify-between py-1"><span>Max extra days</span><span className="font-medium">{result.config.maxExtraDays}d</span></div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Container ─────────────────────────────────────────────────────────────────
const ActingDriverFare = () => {
  const { hasPermission } = useAuth();
  const [view, setView] = useState('list');
  const [editing, setEditing] = useState(null);

  // Shared with the simulator's region picker. Same query key as the list, so it
  // is served from cache rather than refetched when switching views.
  const { data: cfgRes } = useQuery(['acting-driver-fare'], () => api.get('/acting-driver-fare'));
  const allConfigs = cfgRes?.data?.data?.configs || [];

  if (!hasPermission('actingDriverFare', 'view')) {
    return (
      <div className="empty-state">
        <h3 className="empty-state-title">Access Denied</h3>
        <p className="empty-state-description">You don't have permission to view fare configuration.</p>
      </div>
    );
  }

  const canWrite = hasPermission('actingDriverFare', 'write');
  const canDelete = hasPermission('actingDriverFare', 'delete');

  return (
    <div className="space-y-6">
      <div className="page-header">
        <div className="px-4 py-5 sm:px-6 flex items-center justify-between">
          <div>
            {view !== 'list' && (
              <button className="text-sm text-gray-500 flex items-center mb-1" onClick={() => { setView('list'); setEditing(null); }}>
                <ArrowLeft className="h-4 w-4 mr-1" /> Back
              </button>
            )}
            <h1 className="page-title">
              {view === 'simulate' ? 'Acting Driver Fare Simulator' : 'Acting Driver Fare Configuration'}
            </h1>
            <p className="page-subtitle">
              {view === 'simulate'
                ? 'Price a hypothetical trip through the live fare service — no booking required'
                : 'Region-wise acting-driver pricing (day/hour rates, reach, round trip)'}
            </p>
          </div>
          {view === 'list' && (
            <div className="flex gap-2">
              <button className="btn btn-secondary" onClick={() => setView('simulate')}>
                <Calculator className="h-4 w-4 mr-2" /> Fare Simulator
              </button>
              {canWrite && (
                <button className="btn btn-primary" onClick={() => { setEditing(null); setView('create'); }}>
                  <Plus className="h-4 w-4 mr-2" /> New Configuration
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {view === 'list' && (
        <List onEdit={(c) => { setEditing(c); setView('create'); }} onCreate={() => setView('create')} canWrite={canWrite} canDelete={canDelete} />
      )}
      {view === 'simulate' && <Simulator configs={allConfigs} />}
      {view === 'create' && (
        <Form editing={editing} onBack={() => { setView('list'); setEditing(null); }} onSaved={() => { setView('list'); setEditing(null); }} />
      )}
    </div>
  );
};

export default ActingDriverFare;
